import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FileText, CheckCircle, Clock, Package, IndianRupee, Eye, Edit2, Trash2, Download, Plus, Search, ChevronUp, ChevronDown, MoreVertical, XCircle } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { useBusiness } from '../../contexts/BusinessContext';
import { getApiErrorMessage } from '../../utils/apiErrors';
import ConfirmModal from '../../components/modals/ConfirmModal';
import ColumnFilter from '../../components/shared/ColumnFilter';
import ListPageHeader from '../../components/shared/ListPageHeader';
import { purchaseReceivesAPI } from '../../services/part2-api';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

const PurchaseReceivesPage = () => {
  const { businessId } = useParams();
  const navigate = useNavigate();
  const { activeBusiness } = useBusiness();
  const [receives, setReceives] = useState([]);
  const [stats, setStats] = useState({ total: 0, draft: 0, confirmed: 0, total_value: 0 });
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [isHeaderCollapsed, setIsHeaderCollapsed] = useState(false);
  
  // Column filters
  const [columnFilters, setColumnFilters] = useState({
    grn_number: '',
    supplier_name: '',
    receive_date: { min: '', max: '' },
    status: [],
    total_quantity: { min: '', max: '' }
  });

  const [confirmModal, setConfirmModal] = useState(null);
  const bid = businessId || activeBusiness?.id;

  const fetchReceives = useCallback(async () => {
    if (!bid) return;
    setLoading(true);
    try {
      const params = {};
      if (statusFilter !== 'all') params.status = statusFilter.toUpperCase();
      if (searchQuery) params.search = searchQuery;

      const response = await purchaseReceivesAPI.list(params);
      setReceives(response.data.receives || []);
      setStats(response.data.stats || { total: 0, draft: 0, confirmed: 0, total_value: 0 });
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to load purchase receives'));
    } finally {
      setLoading(false);
    }
  }, [bid, statusFilter, searchQuery]);

  useEffect(() => { fetchReceives(); }, [fetchReceives]);

  const getSupplierName = (r) => r.supplier_name || r.party_name || '';
  const getTotalValue = (r) => r.grand_total || r.total_value || 0;

  const filteredReceives = receives.filter(r => {
    const matchesGrn = !columnFilters.grn_number ||
      r.receive_number?.toLowerCase().includes(columnFilters.grn_number.toLowerCase());
    const matchesSupplier = !columnFilters.supplier_name ||
      getSupplierName(r).toLowerCase().includes(columnFilters.supplier_name.toLowerCase());
    const matchesReceiveDate = (!columnFilters.receive_date?.min || new Date(r.receive_date) >= new Date(columnFilters.receive_date.min)) &&
      (!columnFilters.receive_date?.max || new Date(r.receive_date) <= new Date(columnFilters.receive_date.max));
    const matchesStatus = columnFilters.status.length === 0 ||
      columnFilters.status.includes(r.status?.toUpperCase());
    const qty = r.total_quantity || 0;
    const matchesQty = (!columnFilters.total_quantity?.min || qty >= parseFloat(columnFilters.total_quantity.min)) &&
      (!columnFilters.total_quantity?.max || qty <= parseFloat(columnFilters.total_quantity.max));
    return matchesGrn && matchesSupplier && matchesReceiveDate && matchesStatus && matchesQty;
  });

  const handleAdd = () => navigate(`/business/${bid}/purchase-receives/new`);
  const handleView = (receive) => navigate(`/business/${bid}/purchase-receives/${receive.id}`);

  const handleConfirm = (receive) => {
    setConfirmModal({
      title: 'Confirm Purchase Receive',
      description: `Confirm ${receive.receive_number}? This will update inventory stock levels.`,
      confirmLabel: 'Confirm & Update Inventory',
      confirmClass: 'bg-blue-600 hover:bg-blue-700',
      icon: CheckCircle,
      iconBgClass: 'bg-blue-100',
      iconClass: 'text-blue-600',
      onConfirm: async () => {
        setConfirmModal(null);
        try {
          await purchaseReceivesAPI.confirm(receive.id);
          toast.success('Purchase receive confirmed, inventory updated');
          fetchReceives();
        } catch (error) {
          toast.error(getApiErrorMessage(error, 'Failed to confirm'));
        }
      },
    });
  };

  const handleDelete = (receive) => {
    setConfirmModal({
      title: 'Delete Purchase Receive',
      description: `Delete ${receive.receive_number}? This cannot be undone.`,
      confirmLabel: 'Delete',
      confirmClass: 'bg-red-600 hover:bg-red-700',
      icon: Trash2,
      iconBgClass: 'bg-red-100',
      iconClass: 'text-red-600',
      onConfirm: async () => {
        setConfirmModal(null);
        try {
          await purchaseReceivesAPI.delete(receive.id);
          toast.success('Purchase receive deleted');
          fetchReceives();
        } catch (error) {
          toast.error(getApiErrorMessage(error, 'Failed to delete'));
        }
      },
    });
  };

  const handleDownload = async (receive) => {
    try {
      const response = await purchaseReceivesAPI.pdf(receive.id);
      const url = window.URL.createObjectURL(response.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `GRN_${receive.receive_number}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to download PDF'));
    }
  };

  const formatCompactCurrency = (amount) => {
    if (amount >= 10000000) return `₹${(amount / 10000000).toFixed(1)}Cr`;
    if (amount >= 100000) return `₹${(amount / 100000).toFixed(1)}L`;
    if (amount >= 1000) return `₹${(amount / 1000).toFixed(1)}K`;
    return `₹${amount?.toFixed(0) || 0}`;
  };

  const getStatusBadge = (status) => {
    const badges = {
      DRAFT: { bg: 'bg-amber-100', text: 'text-amber-700', label: 'Draft' },
      CONFIRMED: { bg: 'bg-emerald-100', text: 'text-emerald-700', label: 'Confirmed' }
    };
    return badges[status] || badges.DRAFT;
  };

  return (
    <div className="h-full flex flex-col ">
      {/* Premium Header */}
      <ListPageHeader
        title="Purchase Receives"
        stats={[
          { icon: FileText, label: 'Total', value: stats.total, color: 'slate' },
          { icon: Clock, label: 'Draft', value: stats.draft, color: 'amber' },
          { icon: CheckCircle, label: 'Confirmed', value: stats.confirmed, color: 'emerald' },
          { icon: IndianRupee, label: 'Total Value', value: formatCompactCurrency(stats.total_value), color: 'teal' },
        ]}
        ctaLabel="New Purchase Receive"
        onCtaClick={handleAdd}
      />

      {/* TABLE AREA */}
      <div className="flex-1 overflow-hidden flex flex-col px-2 pb-2 pt-2">
        <div className="flex-1 flex flex-col bg-white rounded-md border border-slate-200/60 shadow-sm overflow-hidden">
          <div className="flex-1 flex flex-col overflow-hidden">
            
            {loading ? (
              <div className="flex-1 flex items-center justify-center">
                <div className="flex flex-col items-center gap-3">
                  <div className="w-8 h-8 border-[3px] border-slate-200 border-t-teal-600 rounded-full animate-spin"></div>
                  <p className="text-[13px] text-slate-500 font-normal">Loading receives...</p>
                </div>
              </div>
            ) : filteredReceives.length === 0 ? (
              <div className="flex-1 flex flex-col items-center justify-center">
                <div className="w-14 h-14 bg-slate-100 rounded-xl flex items-center justify-center mb-4">
                  <Package className="w-7 h-7 text-slate-400" />
                </div>
                <p className="text-[15px] font-medium text-slate-600">No purchase receives yet</p>
                <p className="text-[13px] text-slate-400 mt-1 mb-4">Create a purchase receive from a confirmed purchase order</p>
                <button
                  onClick={handleAdd}
                  className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors text-[13px] font-medium"
                >
                  Create Purchase Receive
                </button>
              </div>
            ) : (
              <div className="flex-1 flex flex-col overflow-hidden">
                {/* TABLE HEADER */}
                <div className="flex-shrink-0">
                  <table className="tl-table">
                    <thead>
                      <tr>
                        <th className="tl-th bg-teal-50/60 text-teal-700 border-b border-teal-100" style={{ width: '12%' }}>
                          <div className="flex items-center gap-1">
                            Receive #
                            <ColumnFilter 
                              type="text" 
                              value={columnFilters.grn_number}
                              onChange={(val) => setColumnFilters({...columnFilters, grn_number: val})}
                              placeholder="Filter..."
                            />
                          </div>
                        </th>
                        <th className="tl-th bg-teal-50/60 text-teal-700 border-b border-teal-100" style={{ width: '12%' }}>PO #</th>
                        <th className="tl-th bg-teal-50/60 text-teal-700 border-b border-teal-100" style={{ width: '20%' }}>
                          <div className="flex items-center gap-1">
                            Supplier
                            <ColumnFilter 
                              type="text" 
                              value={columnFilters.supplier_name}
                              onChange={(val) => setColumnFilters({...columnFilters, supplier_name: val})}
                              placeholder="Filter..."
                            />
                          </div>
                        </th>
                        <th className="tl-th bg-teal-50/60 text-teal-700 border-b border-teal-100" style={{ width: '12%' }}>
                          <div className="flex items-center gap-1">
                            Receive Date
                            <ColumnFilter 
                              type="date" 
                              value={columnFilters.receive_date}
                              onChange={(val) => setColumnFilters({...columnFilters, receive_date: val})}
                            />
                          </div>
                        </th>
                        <th className="tl-th bg-teal-50/60 text-teal-700 border-b border-teal-100" style={{ width: '10%' }}>
                          <div className="flex items-center gap-1">
                            Status
                            <ColumnFilter 
                              type="status" 
                              value={columnFilters.status}
                              onChange={(val) => setColumnFilters({...columnFilters, status: val})}
                              options={[
                                { value: 'DRAFT', label: 'Draft' },
                                { value: 'CONFIRMED', label: 'Confirmed' }
                              ]}
                            />
                          </div>
                        </th>
                        <th className="tl-th bg-teal-50/60 text-teal-700 border-b border-teal-100 tl-th-right" style={{ width: '14%' }}>Amount</th>
                        <th className="tl-th bg-teal-50/60 text-teal-700 border-b border-teal-100" style={{ width: '20%' }}>Actions</th>
                      </tr>
                    </thead>
                  </table>
                </div>

                {/* TABLE BODY */}
                <div className="flex-1 overflow-y-auto">
                  <table className="tl-table">
                    <tbody className="animate-stagger">
                      {filteredReceives.map((receive, index) => {
                        const statusBadge = getStatusBadge(receive.status);
                        return (
                          <tr 
                            key={receive.id} 
                            className={`tl-tr tl-tr-clickable hover:bg-teal-50/40 animate-tableRowFadeIn ${index % 2 === 0 ? '' : 'bg-[#FAFBFC]'}`}
                            onClick={() => handleView(receive)}
                            style={{ animationDelay: `${Math.min(index * 30, 300)}ms` }}
                          >
                            <td className="tl-td" style={{ width: '12%' }}>
                              <div className="flex items-center gap-3">
                                <div className="w-8 h-8 rounded-full bg-teal-100 flex items-center justify-center flex-shrink-0">
                                  <span className="text-[12px] font-medium text-teal-700">
                                    {receive.receive_number?.slice(-2) || 'PR'}
                                  </span>
                                </div>
                                <span className="tl-cell-primary">{receive.receive_number}</span>
                              </div>
                            </td>
                            <td className="tl-td tl-cell-regular" style={{ width: '12%' }}>
                              <span className="text-teal-600 font-medium">{receive.po_number || '—'}</span>
                            </td>
                            <td className="tl-td tl-cell-regular" style={{ width: '20%' }}>
                              {getSupplierName(receive) || '-'}
                            </td>
                            <td className="tl-td tl-cell-regular" style={{ width: '12%' }}>
                              {receive.receive_date ? new Date(receive.receive_date).toLocaleDateString('en-IN') : '—'}
                            </td>
                            <td className="tl-td" style={{ width: '10%' }}>
                              <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium ${statusBadge.bg} ${statusBadge.text}`}>
                                {statusBadge.label}
                              </span>
                            </td>
                            <td className="tl-td tl-td-right tl-cell-regular" style={{ width: '14%' }}>
                              {'\u20B9'}{getTotalValue(receive).toLocaleString('en-IN')}
                            </td>
                            <td className="tl-td" style={{ width: '5%' }} onClick={(e) => e.stopPropagation()}>
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <button className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-md transition-colors">
                                    <MoreVertical className="w-4 h-4" />
                                  </button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end" className="w-48">
                                  <DropdownMenuItem onClick={() => handleView(receive)}>
                                    <Eye className="w-4 h-4 mr-2" /> View Details
                                  </DropdownMenuItem>
                                  {(receive.status || '').toUpperCase() === 'DRAFT' && (
                                    <DropdownMenuItem onClick={() => handleConfirm(receive)}>
                                      <CheckCircle className="w-4 h-4 mr-2" /> Confirm
                                    </DropdownMenuItem>
                                  )}
                                  <DropdownMenuItem onClick={() => handleDownload(receive)}>
                                    <Download className="w-4 h-4 mr-2" /> Download PDF
                                  </DropdownMenuItem>
                                  {(receive.status || '').toUpperCase() === 'DRAFT' && (
                                    <>
                                      <DropdownMenuSeparator />
                                      <DropdownMenuItem onClick={() => handleDelete(receive)} className="text-red-600 focus:text-red-600">
                                        <Trash2 className="w-4 h-4 mr-2" /> Delete
                                      </DropdownMenuItem>
                                    </>
                                  )}
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {/* TABLE FOOTER */}
                <div className="flex-shrink-0 px-4 py-3 bg-slate-50/80 border-t border-slate-100 flex items-center justify-between text-[12px] text-slate-500">
                  <p>
                    Showing <span className="font-medium text-slate-700">{filteredReceives.length}</span> receives
                  </p>
                  <p>
                    Total Value: <span className="font-medium text-slate-700">{formatCompactCurrency(stats.total_value)}</span>
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <ConfirmModal
        isOpen={!!confirmModal}
        title={confirmModal?.title}
        description={confirmModal?.description}
        confirmLabel={confirmModal?.confirmLabel}
        confirmClass={confirmModal?.confirmClass}
        icon={confirmModal?.icon}
        iconBgClass={confirmModal?.iconBgClass}
        iconClass={confirmModal?.iconClass}
        onConfirm={confirmModal?.onConfirm}
        onClose={() => setConfirmModal(null)}
      />
    </div>
  );
};

export default PurchaseReceivesPage;
